/**
 * scrypt key derivation function implementation
 * Uses Web Crypto API's PBKDF2 for the PBKDF2 portions
 * Implements Salsa20/8 core and ROMix/BlockMix in JavaScript
 */

// PBKDF2-HMAC-SHA256 using SubtleCrypto
async function pbkdf2(
  password: Uint8Array,
  salt: Uint8Array,
  iterations: number,
  keyLength: number
): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey('raw', password, 'PBKDF2', false, [
    'deriveBits',
  ])

  const derived = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      salt: salt,
      iterations: iterations,
    },
    key,
    keyLength * 8
  )

  return new Uint8Array(derived)
}

// Salsa20/8 core function (8 rounds of Salsa20)
function salsa20_8(B: Uint32Array): void {
  const x = new Uint32Array(16)
  for (let i = 0; i < 16; i++) {
    x[i] = B[i]
  }

  // 8 rounds (4 double-rounds)
  for (let i = 0; i < 8; i += 2) {
    // Column round
    x[4] ^= rotl(x[0] + x[12], 7)
    x[8] ^= rotl(x[4] + x[0], 9)
    x[12] ^= rotl(x[8] + x[4], 13)
    x[0] ^= rotl(x[12] + x[8], 18)

    x[9] ^= rotl(x[5] + x[1], 7)
    x[13] ^= rotl(x[9] + x[5], 9)
    x[1] ^= rotl(x[13] + x[9], 13)
    x[5] ^= rotl(x[1] + x[13], 18)

    x[14] ^= rotl(x[10] + x[6], 7)
    x[2] ^= rotl(x[14] + x[10], 9)
    x[6] ^= rotl(x[2] + x[14], 13)
    x[10] ^= rotl(x[6] + x[2], 18)

    x[3] ^= rotl(x[15] + x[11], 7)
    x[7] ^= rotl(x[3] + x[15], 9)
    x[11] ^= rotl(x[7] + x[3], 13)
    x[15] ^= rotl(x[11] + x[7], 18)

    // Row round
    x[1] ^= rotl(x[0] + x[3], 7)
    x[2] ^= rotl(x[1] + x[0], 9)
    x[3] ^= rotl(x[2] + x[1], 13)
    x[0] ^= rotl(x[3] + x[2], 18)

    x[6] ^= rotl(x[5] + x[4], 7)
    x[7] ^= rotl(x[6] + x[5], 9)
    x[4] ^= rotl(x[7] + x[6], 13)
    x[5] ^= rotl(x[4] + x[7], 18)

    x[11] ^= rotl(x[10] + x[9], 7)
    x[8] ^= rotl(x[11] + x[10], 9)
    x[9] ^= rotl(x[8] + x[11], 13)
    x[10] ^= rotl(x[9] + x[8], 18)

    x[12] ^= rotl(x[15] + x[14], 7)
    x[13] ^= rotl(x[12] + x[15], 9)
    x[14] ^= rotl(x[13] + x[12], 13)
    x[15] ^= rotl(x[14] + x[13], 18)
  }

  for (let i = 0; i < 16; i++) {
    B[i] = (B[i] + x[i]) >>> 0
  }
}

// Rotate left
function rotl(x: number, n: number): number {
  return ((x << n) | (x >>> (32 - n))) >>> 0
}

// XOR two blocks
function blockXor(
  dst: Uint32Array,
  src: Uint32Array,
  srcOffset: number,
  len: number
): void {
  for (let i = 0; i < len; i++) {
    dst[i] ^= src[srcOffset + i]
  }
}

// Copy block
function blockCopy(
  dst: Uint32Array,
  dstOffset: number,
  src: Uint32Array,
  srcOffset: number,
  len: number
): void {
  for (let i = 0; i < len; i++) {
    dst[dstOffset + i] = src[srcOffset + i]
  }
}

// BlockMix function
function blockMix(B: Uint32Array, Y: Uint32Array, r: number): void {
  const blockWords = 16 // 64 bytes = 16 uint32
  const X = new Uint32Array(blockWords)

  // X = B[2r-1]
  blockCopy(X, 0, B, (2 * r - 1) * blockWords, blockWords)

  for (let i = 0; i < 2 * r; i++) {
    // X = X XOR B[i]
    blockXor(X, B, i * blockWords, blockWords)
    // X = Salsa20/8(X)
    salsa20_8(X)
    // Y[i] = X
    blockCopy(Y, i * blockWords, X, 0, blockWords)
  }

  // B' = (Y[0], Y[2], ..., Y[2r-2], Y[1], Y[3], ..., Y[2r-1])
  for (let i = 0; i < r; i++) {
    blockCopy(B, i * blockWords, Y, 2 * i * blockWords, blockWords)
  }
  for (let i = 0; i < r; i++) {
    blockCopy(B, (r + i) * blockWords, Y, (2 * i + 1) * blockWords, blockWords)
  }
}

// Integerify: extract integer from block (last 64 bytes interpreted as little-endian)
function integerify(B: Uint32Array, r: number): number {
  const blockWords = 16
  // Get the last block's first word (little-endian interpretation)
  return B[(2 * r - 1) * blockWords]
}

// ROMix function
function romix(B: Uint32Array, r: number, N: number, V: Uint32Array): void {
  const blockSize = 2 * r * 16 // in uint32 words
  const Y = new Uint32Array(blockSize)

  // Step 1: Initialize V
  for (let i = 0; i < N; i++) {
    blockCopy(V, i * blockSize, B, 0, blockSize)
    blockMix(B, Y, r)
  }

  // Step 2: Mix
  for (let i = 0; i < N; i++) {
    const j = integerify(B, r) & (N - 1)
    blockXor(B, V, j * blockSize, blockSize)
    blockMix(B, Y, r)
  }
}

// Convert Uint8Array to Uint32Array (little-endian)
function bytesToUint32(bytes: Uint8Array): Uint32Array {
  const len = bytes.length >>> 2
  const result = new Uint32Array(len)
  for (let i = 0; i < len; i++) {
    result[i] =
      bytes[i * 4] |
      (bytes[i * 4 + 1] << 8) |
      (bytes[i * 4 + 2] << 16) |
      (bytes[i * 4 + 3] << 24)
  }
  return result
}

// Convert Uint32Array to Uint8Array (little-endian)
function uint32ToBytes(words: Uint32Array): Uint8Array {
  const result = new Uint8Array(words.length * 4)
  for (let i = 0; i < words.length; i++) {
    result[i * 4] = words[i] & 0xff
    result[i * 4 + 1] = (words[i] >>> 8) & 0xff
    result[i * 4 + 2] = (words[i] >>> 16) & 0xff
    result[i * 4 + 3] = (words[i] >>> 24) & 0xff
  }
  return result
}

export interface ScryptParams {
  N: number // CPU/memory cost parameter (must be power of 2)
  r: number // Block size parameter
  p: number // Parallelization parameter
  dkLen: number // Desired key length in bytes
}

/**
 * scrypt key derivation function
 * @param password - Password bytes
 * @param salt - Salt bytes
 * @param params - scrypt parameters (N, r, p, dkLen)
 * @returns Derived key
 */
export async function scrypt(
  password: Uint8Array,
  salt: Uint8Array,
  params: ScryptParams
): Promise<Uint8Array> {
  const { N, r, p, dkLen } = params

  // Validate N is a power of 2
  if (N <= 1 || (N & (N - 1)) !== 0) {
    throw new Error('N must be a power of 2 greater than 1')
  }

  const blockSize = 128 * r // in bytes
  const blockSizeWords = 32 * r // in uint32 words

  // Step 1: Generate initial data using PBKDF2
  const B = await pbkdf2(password, salt, 1, p * blockSize)
  const BWords = bytesToUint32(B)

  // Allocate V (the large memory buffer)
  const V = new Uint32Array(N * blockSizeWords)

  // Step 2: Apply ROMix to each block
  for (let i = 0; i < p; i++) {
    const block = new Uint32Array(blockSizeWords)
    blockCopy(block, 0, BWords, i * blockSizeWords, blockSizeWords)
    romix(block, r, N, V)
    blockCopy(BWords, i * blockSizeWords, block, 0, blockSizeWords)
  }

  // Step 3: Finalize with PBKDF2
  const BFinal = uint32ToBytes(BWords)
  return pbkdf2(password, BFinal, 1, dkLen)
}

/**
 * Derive key from passphrase using scrypt
 * @param passphrase - User's passphrase
 * @param salt - Random salt
 * @param params - scrypt parameters
 * @returns Derived key
 */
export async function deriveKey(
  passphrase: string,
  salt: Uint8Array,
  params: ScryptParams
): Promise<Uint8Array> {
  const encoder = new TextEncoder()
  const passwordBytes = encoder.encode(passphrase)
  return scrypt(passwordBytes, salt, params)
}
